import React from 'react';
import PropTypes from 'prop-types';

import styled from '@emotion/styled';

import Tick from '@zego/static/svgs/Tick';
import {
  BOX_SHADOW,
  disabledColor,
  modernEraFamily,
  primaryColor,
  unit,
  white,
} from '@zego/theme';

import ButtonSpinner from './ButtonSpinner';

const getSecondaryColours = disabled => {
  if (disabled) {
    return {
      color: disabledColor(6),
      backgroundColor: disabledColor(2),
      borderColor: disabledColor(2),
      ':active': {
        transform: 'none',
      },
    };
  }
  return {
    backgroundColor: primaryColor(3),
    borderColor: primaryColor(3),
    ':hover': {
      backgroundColor: primaryColor(4),
      borderColor: primaryColor(4),
    },
    ':active': {
      backgroundColor: primaryColor(5),
      borderColor: primaryColor(5),
    },
    color: primaryColor(9),
  };
};

const getPrimaryColours = disabled => {
  if (disabled) {
    return {
      color: disabledColor(8),
      backgroundColor: disabledColor(4),
      borderColor: disabledColor(4),
      ':active': {
        transform: 'none',
      },
    };
  }
  return {
    borderColor: primaryColor(8),
    backgroundColor: primaryColor(8),
    boxShadow: BOX_SHADOW[0],
    transition: 'all .4s ease-in-out',
    ':hover': {
      backgroundColor: primaryColor(7),
      borderColor: primaryColor(7),
      boxShadow: BOX_SHADOW[2],
      transition: 'all .2s ease-in-out',
    },
    ':active': {
      backgroundColor: primaryColor(9),
      borderColor: primaryColor(9),
    },
    color: white,
  };
};

const Container = styled('button')(
  {
    display: 'inline-flex',
    flexDirection: 'row',
    borderWidth: 1,
    borderStyle: 'solid',
    lineHeight: 1,
    transition: '0.1s ease all',
    textDecoration: 'none',
    position: 'relative',
    minWidth: 100,
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    fontWeight: 500,
    borderRadius: 8,
    fontFamily: modernEraFamily,
    ':focus': {
      outline: 'none',
    },
    ':active': {
      transform: 'scale(0.975)',
    },
  },
  props => {
    return {
      padding: props.padding || '22px 32px 22px 32px',
      fontSize: props.fontSize || 18,
    };
  },
  ({ disabled, stretch, secondary }) => {
    const baseRules = {
      cursor: disabled ? 'auto' : 'pointer',
      width: stretch ? '100%' : 'auto',
    };

    if (secondary) {
      return { ...baseRules, ...getSecondaryColours(disabled) };
    } else {
      return { ...baseRules, ...getPrimaryColours(disabled) };
    }
  },
);

const ReplacementIconContainer = styled('div')`
  position: absolute;
`;

class Button extends React.PureComponent {
  static defaultProps = {
    submitting: false,
    disabled: false,
    outline: false,
  };

  render() {
    const {
      children,
      onClick,
      submitting,
      success,
      disabled,
      outline,
      icon,
      padding,
      fontSize,
      spinnerColor,
      testId,
      ...other
    } = this.props;
    return (
      <Container
        onClick={!disabled && !success ? onClick : () => null}
        disabled={disabled}
        submitting={submitting}
        outline={outline}
        padding={padding}
        fontSize={fontSize}
        data-testid={testId}
        {...other}
      >
        {icon ? (
          <div
            css={{ visibility: submitting || success ? 'hidden' : 'visible' }}
          >
            {typeof icon === 'string' ? (
              <img
                alt=""
                aria-hidden={true}
                src={icon}
                css={{ paddingRight: unit(1) }}
              />
            ) : (
              <div
                css={{
                  paddingRight: unit(1),
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {icon}
              </div>
            )}
          </div>
        ) : null}
        {submitting && (
          <ReplacementIconContainer>
            <ButtonSpinner color={spinnerColor || undefined} />
          </ReplacementIconContainer>
        )}
        {success && (
          <ReplacementIconContainer>
            <Tick color={spinnerColor || white} />
          </ReplacementIconContainer>
        )}
        <div
          css={{
            visibility: submitting || success ? 'hidden' : 'visible',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          {children}
        </div>
      </Container>
    );
  }
}

Button.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  submitting: PropTypes.bool,
  success: PropTypes.bool,
  disabled: PropTypes.bool,
  outline: PropTypes.bool,
  stretch: PropTypes.bool,
  icon: PropTypes.node,
  padding: PropTypes.string,
  fontSize: PropTypes.number,
  spinnerColor: PropTypes.string,
};
Button.defaultProps = {
  isLoading: false,
};

export default Button;
